/**
 * 🌙 The asleep look (W2b, treatment C = honest + keep the lights on, Nina
 * 09-26; mockup https://claude.ai/artifact/S8akGF3U7xM1zRBgSMmd5n, the "Out"
 * and "A visitor comes by" frames). Pure: the status payload's top-level
 * `ai_power` block (backend src/services/AiPower.js statusBlock, present only
 * while HOUSE_ASLEEP_ENABLED is on) and the asleep/wake cards' data → what the
 * page shows. No block = awake = every surface exactly as before, so this
 * ships dark by construction and an old backend is safe. Every space reads it:
 * one OpenRouter key feeds the whole fleet, so at $0 everyone sleeps.
 *
 * Copy rules (Nina + the asleep handoff §1.3): short, no em dashes, NEVER the
 * word "credits" (in the house that is the per-round speech credit), no
 * promised wake time, residents never ask for money, and a support link never
 * without its note (Nina 09-26: the money only runs the house and pays
 * nobody; the note comes from the backend so every surface says the same).
 *
 * 🔋 09-29 the ONE exception: a sleep WE scheduled (the 5 AM / 5 PM recharge
 * window, the block's asleep_reason 'window' + next_recharge_at) says when it
 * ends, in the viewer's clock: "Resting since 3:12 PM · back at 5 PM". A real
 * $0 (reason 'balance', or no reason) keeps "back after the next top-up".
 */
import { clockIn, clockParts, rechargeView, lightsLabel } from './powerMeter';
import { chipInUrl } from './chipIn';

export const ASLEEP_COPY = {
  play: 'Asleep',                                  // the Poke / Play button's word (💤 is its icon)
  playTitle: 'Everyone’s asleep',
  placeholder: 'Leave a note for when they wake…',
  notesLeft: (n) => `${n} ${n === 1 ? 'note' : 'notes'} left till they wake`, // the free-chat budget line (rounds stand still while asleep)
  notesDone: 'Notes saved. More when they wake.',
  receipt: '💤 Saved for when they wake',
  status: 'asleep',                                // every resident's status line (😴 is the line's own glyph)
  back: 'back after the next top-up',
  backAt: (clock) => `back at ${clock}`,           // 🔋 ONLY a 'window' sleep (the recharge we schedule)
  cardSupport: 'Help keep the lights on',
  bannerSupport: 'Keep the lights on',
};

/** Is the fleet asleep? The status block's own flag (level 'asleep' as a belt). */
export function isAsleep(p) {
  return !!(p && typeof p === 'object' && (p.asleep === true || p.level === 'asleep'));
}

/** A support link only ever travels WITH its note (https only). */
export function supportOf(url, note, label) {
  const u = typeof url === 'string' && /^https:\/\/\S+$/i.test(url) ? url : null;
  const n = typeof note === 'string' && note.trim() ? note.trim() : null;
  return u && n ? { url: u, note: n, label } : null;
}

/**
 * The line above the message box: "Resting since 4:20pm · back after the next
 * top-up" (the component draws the 💤). The time is in `tz` (the house's own
 * clock) or the viewer's when there is none; unreadable = no time at all.
 * 🔋 A 'window' sleep with a readable next_recharge_at: "Resting since 3:12 PM
 * · back at 5 PM", both times in the viewer's clock (`viewerTz`, null = theirs).
 * 💳 10-28: the link names the room (`gameId`, utils/chipIn.chipInUrl), and a
 * room a mod switched off (`chipInOff`) shows none.
 */
export function restingBanner(p, { tz = null, viewerTz = null, now = Date.now(), spaceName = null, gameId = null, chipInOff = false } = {}) {
  if (!isAsleep(p)) return null;
  const label = spaceName ? lightsLabel(spaceName, { short: true }) : ASLEEP_COPY.bannerSupport;
  const link = chipInOff === true ? null : chipInUrl(p.support_url, gameId);
  const recharge = p.asleep_reason === 'window' ? rechargeView(p.next_recharge_at, { viewerTz, now }) : null;
  if (recharge) {
    const ran = clockParts(Date.parse(p.since || ''), viewerTz);
    return {
      text: `${ran ? `Resting since ${ran.full}` : 'Resting'} · ${ASLEEP_COPY.backAt(recharge.short)}`,
      support: supportOf(link, p.support_note, label),
    };
  }
  const at = clockIn(p.since, tz);
  return {
    text: `${at ? `Resting since ${at}` : 'Resting'} · ${ASLEEP_COPY.back}`,
    support: supportOf(link, p.support_note, label),
  };
}

/**
 * Every resident's status reads asleep while the fleet is: the ONE map every
 * LocalStatusLine surface reads, so the chat rows, the locals cards and the
 * dossier agree. Awake = the same object back (memo-stable).
 */
export function asleepStatuses(statuses, asleep) {
  if (!asleep || !statuses || typeof statuses !== 'object') return statuses;
  const out = {};
  Object.keys(statuses).forEach((id) => {
    const st = statuses[id];
    out[id] = st && typeof st === 'object' ? { ...st, awake: false, line: ASLEEP_COPY.status } : st;
  });
  return out;
}

const CARD_HEADS = ['🌙', '☀️', '☀'];

/**
 * An asleep/wake card (a game_announcement whose data carries `ai_power`) →
 * { kind, emoji, text, support } with the page's honest `page_message` (the
 * residents' transcript keeps the fiction `message`); null for every other card.
 */
export function powerCard(data, { spaceName = null } = {}) {
  if (!data || (data.ai_power !== 'asleep' && data.ai_power !== 'wake')) return null;
  const raw = String(data.page_message || data.message || '').trim();
  if (!raw) return null;
  let text = raw;
  const head = CARD_HEADS.find((h) => text.startsWith(h));
  if (head) text = text.slice(head.length).trim() || raw;
  const asleep = data.ai_power === 'asleep';
  return {
    kind: data.ai_power,
    emoji: asleep ? '🌙' : '☀️',
    text,
    support: asleep ? supportOf(data.support_url, data.support_note, spaceName ? lightsLabel(spaceName) : ASLEEP_COPY.cardSupport) : null,
  };
}

const NOTE_TYPES = new Set(['public_message', 'human_interjection', 'private_message', 'secret_message']);

/**
 * Which of the viewer's lines carries "💤 Saved for when they wake": the
 * NEWEST note they sent since the fleet fell asleep (one receipt, never a row
 * of them). `rounds` = the page's results ([{ actions }]); an optimistic row
 * counts, so the receipt shows the moment they send.
 */
export function receiptFor(rounds, meId, p) {
  if (!isAsleep(p) || !meId || !Array.isArray(rounds)) return null;
  const since = Date.parse(p.since || '');
  if (!Number.isFinite(since)) return null;
  for (let r = rounds.length - 1; r >= 0; r--) {
    const acts = (rounds[r] && rounds[r].actions) || [];
    for (let i = acts.length - 1; i >= 0; i--) {
      const a = acts[i];
      if (!a || a.game_instance_player_id !== meId || !NOTE_TYPES.has(a.type)) continue;
      if (!a.data || !a.data.message) continue;
      const t = Date.parse(a.created_at || '');
      return Number.isFinite(t) && t >= since ? (a.id || null) : null;
    }
  }
  return null;
}
